/**
 * Announcement rule definition.
 */
export interface AnnouncementRule {
  msg: string;
  style?: "info" | "warn";
  path?: string;
}

/**
 * Frontend configuration from Vercel Edge Config.
 */
export interface FrontendConfig {
  announcements?: AnnouncementRule[];
}

/**
 * Normalizes the announcement style to a valid value.
 * Unknown values are treated as "warn".
 */
function normalizeStyle(style?: string): "info" | "warn" {
  if (style === "info" || style === "warn") {
    return style;
  }
  return "warn";
}

/**
 * Gets the matching announcement for the given pathname from the frontend config.
 * Rules are evaluated in array order, and the first matching rule wins.
 *
 * @param pathname - The pathname to match against (e.g., "/donate", "/")
 * @param frontendConfig - The frontend configuration containing announcements
 * @returns The matching announcement rule with normalized style, or undefined if no match
 */
export function getAnnouncementForPath(
  pathname: string,
  frontendConfig?: FrontendConfig
): AnnouncementRule | undefined {
  if (
    !frontendConfig?.announcements ||
    !Array.isArray(frontendConfig.announcements)
  ) {
    return undefined;
  }

  // Evaluate rules in order, first match wins
  for (const rule of frontendConfig.announcements) {
    if (!rule.msg) {
      // Skip rules without a message
      continue;
    }

    // If no path specified, matches all pages
    if (!rule.path) {
      return { ...rule, style: normalizeStyle(rule.style) };
    }

    // Try to match the regex
    try {
      const regex = new RegExp(rule.path);
      if (regex.test(pathname)) {
        return { ...rule, style: normalizeStyle(rule.style) };
      }
    } catch {
      // Invalid regex, skip this rule
      continue;
    }
  }

  return undefined;
}
